import { getAccountActiveCount } from "@/api/accountCount";
import { useSidebarCountStore } from "@/store/pinia/sidebarCount";
import launchDarklyService from "@/services/launchdarkly";
import { LD_FLAG_IDEA_MDP_622 } from "@components/constants/launchDarklyFlagKeys";

/**
 * Trailing debounce for the background refresh (ER-12439). A burst of lifecycle
 * actions (bulk retire, rapid activate/archive clicks) coalesces into one
 * active-counts request instead of one per mutation.
 */
export const ACCOUNT_COUNTS_REFRESH_DEBOUNCE_MS = 300;

/** Pending debounce timers, keyed by account id. */
const pendingRefreshes = new Map();
/** In-flight requests, keyed by account id (single-flight). */
const inFlightRefreshes = new Map();
/** Sequence of the newest started request per account (last-write-wins). */
const refreshSequence = new Map();

const accountKey = (accountId) => String(accountId);

/**
 * ER-12439 — is the non-blocking, single-flight counts refresh enabled?
 *
 * Gated by idea-MDP-622 (the Organization dashboard rollout gate). Read
 * synchronously so the mutation helpers never wait on the flag SDK, and it
 * defaults to `false`: if the flag cannot be evaluated (SDK not ready, network
 * error, missing key) the legacy awaited behaviour is preserved.
 *
 * @returns {boolean}
 */
export const isNonBlockingAccountCountsEnabled = () =>
  launchDarklyService.getFlagSync(LD_FLAG_IDEA_MDP_622, false) === true;

/**
 * Fetches the active counts and stores them, guarding against a stale response
 * overwriting a newer one.
 *
 * @param {string|number} accountId
 * @returns {Promise<void>}
 */
const fetchAndStoreAccountCounts = async (accountId) => {
  const key = accountKey(accountId);
  const sequence = (refreshSequence.get(key) || 0) + 1;
  refreshSequence.set(key, sequence);

  const { data: accountCounts } = await getAccountActiveCount(accountId);
  // Last write wins: a newer refresh started while this response was in flight,
  // so these counts may be older than what the store already holds.
  if (!accountCounts || refreshSequence.get(key) !== sequence) {
    return;
  }

  const sidebarCountStore = useSidebarCountStore();
  sidebarCountStore.setAccountCounts(accountCounts);
};

/**
 * Runs one refresh for an account, reusing the promise of a request that is
 * already in flight (single-flight).
 *
 * @param {string|number} accountId
 * @returns {Promise<void>}
 */
const runAccountCountsRefresh = (accountId) => {
  const key = accountKey(accountId);
  const existingRequest = inFlightRefreshes.get(key);
  if (existingRequest) {
    return existingRequest;
  }

  const request = fetchAndStoreAccountCounts(accountId)
    .catch((error) => {
      console.error("Error updating account counts:", error);
    })
    .finally(() => {
      inFlightRefreshes.delete(key);
    });

  inFlightRefreshes.set(key, request);
  return request;
};

/**
 * Schedules a trailing-debounced background refresh.
 *
 * If a request is already in flight, the scheduled run waits for it and then
 * refreshes once more, so mutations that land while a request is open are not
 * lost (they are folded into the trailing request instead of a parallel one).
 *
 * @param {string|number} accountId
 */
const scheduleAccountCountsRefresh = (accountId) => {
  const key = accountKey(accountId);

  clearTimeout(pendingRefreshes.get(key));

  const timer = setTimeout(() => {
    pendingRefreshes.delete(key);

    if (inFlightRefreshes.has(key)) {
      // Refresh once more when the open request settles (the `.finally` inside
      // `runAccountCountsRefresh` clears the entry first).
      inFlightRefreshes.get(key).finally(() => scheduleAccountCountsRefresh(accountId));
      return;
    }

    runAccountCountsRefresh(accountId);
  }, ACCOUNT_COUNTS_REFRESH_DEBOUNCE_MS);

  pendingRefreshes.set(key, timer);
};

/**
 * Updates account counts by calling the active account API and storing the data.
 * IMPORTANT: This function fetches counts that are INDEPENDENT of any filters
 * applied to employee/position/role/responsibility lists. The counts should
 * always reflect the total active counts regardless of current filter state.
 *
 * Always awaited: kept for callers that explicitly need fresh counts before
 * continuing (login / account switch).
 *
 * @param {string|number} account_id - The account ID
 * @returns {Promise<void>}
 */
export const updateAccountCounts = async (account_id) => {
  try {
    await fetchAndStoreAccountCounts(account_id);
  } catch (error) {
    console.error("Error updating account counts:", error);
  }
};

/**
 * ER-12439 — refresh the account counts after an object lifecycle mutation.
 *
 * Flag on: single-flight + ~300 ms trailing debounce + last-write-wins guard,
 * so callers resolve as soon as the mutation response arrives and the counts
 * land in the shared sidebar store in the background. A burst of N mutations
 * sends exactly one request.
 *
 * Flag off: identical to the legacy behaviour (awaits the active-counts call),
 * so rolling the flag back restores the old timing exactly.
 *
 * @param {string|number} account_id - The account ID
 * @returns {Promise<void>}
 */
export const refreshAccountCounts = async (account_id) => {
  if (account_id == null || account_id === "") {
    return;
  }

  if (!isNonBlockingAccountCountsEnabled()) {
    await updateAccountCounts(account_id);
    return;
  }

  scheduleAccountCountsRefresh(account_id);
};

/**
 * Updates account counts on login/account selection
 * This should be called after a user logs in and selects an account
 * @param {string|number} account_id - The account ID
 * @returns {Promise<void>}
 */
export const updateAccountCountsOnLogin = async (account_id) => {
  if (!account_id) {
    console.warn("No account ID provided for account count update");
    return;
  }

  // A login/switch starts a new session for the counts: drop any queued or
  // in-flight refresh from the previous account before loading this one.
  resetAccountCountsRefresh();

  await updateAccountCounts(account_id);
};

/**
 * Drops pending timers and in-flight bookkeeping. Used by tests and when the
 * active account changes so a queued refresh cannot land on the new account.
 */
export const resetAccountCountsRefresh = () => {
  pendingRefreshes.forEach((timer) => clearTimeout(timer));
  pendingRefreshes.clear();
  inFlightRefreshes.clear();

  // Bump the sequences instead of clearing them: a response that is already in
  // flight from the previous session then fails the last-write-wins guard, so
  // it cannot overwrite the counts the new session loads.
  refreshSequence.forEach((sequence, key) => refreshSequence.set(key, sequence + 1));
};
